import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import gql from 'graphql-tag';
import {Apollo} from 'apollo-angular';
import {ErrorService} from '../../services/error.service';
import {ToastrService} from 'ngx-toastr';
import {UntypedFormControl, Validators} from '@angular/forms';
import {catchError, EMPTY, map, Subject, Subscription} from 'rxjs';
import {exhaustMap, filter} from 'rxjs/operators';
import {MultiSelectType} from '../../components/multi-select/multi-select.component';
import {UpdateOrCreateLabel} from './__generated__/UpdateOrCreateLabel';
import {UpdateOrCreateTag} from './__generated__/UpdateOrCreateTag';

const UpdateOrCreateLabelMutation = gql`
  mutation UpdateOrCreateLabel($input: LabelInput) {
    updateOrCreateLabel(input: $input) {
      label {
        id
        name
        viewAllContent
      }
      errors {
        field
        messages
      }
    }
  }
`;

const UpdateOrCreateTagMutation = gql`
  mutation UpdateOrCreateTag($input: TagInput) {
    updateOrCreateTag(input: $input) {
      tag {
        id
        name
      }
      errors {
        field
        messages
      }
    }
  }
`;

export interface TagLabelItem {
  id: string;
  name: string;
  viewAllContent?: boolean;
}

@Component({
  selector: 'app-tag-label-title',
  templateUrl: './tag-label-title.component.html',
  styleUrls: ['./tag-label-title.component.scss'],
  standalone: false,
})
export class TagLabelTitleComponent implements OnInit, OnDestroy {
  optionType: MultiSelectType = MultiSelectType.LABEL;
  MultiSelectType = MultiSelectType;
  option: TagLabelItem | null = null;
  titleInput = new UntypedFormControl(null, {validators: Validators.required});
  submitLabel$ = new Subject<string | null>();
  submitTag$ = new Subject<string | null>();
  @Output() changedOption: EventEmitter<TagLabelItem> = new EventEmitter<TagLabelItem>();
  private subscriptions: Subscription[] = [];

  constructor(
    private bsModalRef: BsModalRef,
    private apollo: Apollo,
    private errService: ErrorService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.submitLabel$
        .pipe(
          filter(() => this.titleInput.valid),
          map(() => [this.option?.id, this.titleInput.value]),
          exhaustMap(([id, title]) => {
            return this.apollo.mutate<UpdateOrCreateLabel>({
              mutation: UpdateOrCreateLabelMutation,
              variables: {
                input: {
                  id,
                  name: title,
                },
              },
              update: cache => cache.evict({id: 'ROOT_QUERY', fieldName: 'labels'}),
            });
          }),
          catchError(err => {
            this.errService.showErrorDialog(err);
            return EMPTY;
          }),
        )
        .subscribe(data => {
          this.toastr.success($localize`Successfully created`);
          if (this.option) {
            this.option.name = this.titleInput.value;
            this.changedOption.emit(this.option);
          } else {
            if (data.data && data.data?.updateOrCreateLabel.label) {
              this.changedOption.emit(data.data?.updateOrCreateLabel.label);
            }
          }

          this.closeModal();
        }),
      this.submitTag$
        .pipe(
          filter(() => this.titleInput.valid),
          map(() => [this.option?.id, this.titleInput.value]),
          exhaustMap(([id, title]) => {
            return this.apollo.mutate<UpdateOrCreateTag>({
              mutation: UpdateOrCreateTagMutation,
              variables: {
                input: {
                  id,
                  name: title,
                },
              },
              update: cache => cache.evict({id: 'ROOT_QUERY', fieldName: 'tags'}),
            });
          }),
          catchError(err => {
            this.errService.showErrorDialog(err);
            return EMPTY;
          }),
        )
        .subscribe(data => {
          this.toastr.success($localize`Successfully created`);
          if (this.option) {
            this.option.name = this.titleInput.value;
            this.changedOption.emit(this.option);
          } else {
            if (data.data && data.data?.updateOrCreateTag.tag) {
              this.changedOption.emit(data.data?.updateOrCreateTag.tag);
            }
          }

          this.closeModal();
        }),
    );

    if (this.option) {
      this.titleInput.setValue(this.option.name);
    }
  }

  saveOption() {
    this.optionType === MultiSelectType.LABEL
      ? this.submitLabel$.next(this.titleInput.value)
      : this.submitTag$.next(this.titleInput.value);
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
